import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Plus, RefreshCw, TrendingUp, TrendingDown, Users, ChevronDown, ChevronUp, Home } from 'lucide-react'
import { formatCurrency, cn } from '@/lib/utils'
import api from '@/lib/api'
import type { Holding, InvestorMatch, EtfProfile, InvestorDetail, Property } from '@/types'
import { HoldingsBubbleChart } from '@/components/investments/HoldingsBubbleChart'
import { PriceChart } from '@/components/investments/PriceChart'
import { useViewModeStore } from '@/stores/viewModeStore'

type ManualAccountOption = {
  id: number
  name: string
}

type TickerSearchResult = {
  ticker: string
  name: string
  has_logo?: boolean
}

const defaultManualAccountName = 'Manual Holdings'

const resolveManualAccountId = (account: {
  manual_id?: number
  account_id?: string
  id?: number | string
}) => {
  if (typeof account.manual_id === 'number') {
    return account.manual_id
  }

  const accountKey =
    typeof account.account_id === 'string'
      ? account.account_id
      : typeof account.id === 'string'
        ? account.id
        : ''

  if (accountKey.startsWith('manual_')) {
    const parsed = Number(accountKey.replace('manual_', ''))
    return Number.isFinite(parsed) ? parsed : null
  }

  if (typeof account.id === 'number') {
    return account.id
  }

  return null
}

type TickerLogoProps = {
  ticker: string
  hasLogo?: boolean
}

function TickerLogo({ ticker, hasLogo }: TickerLogoProps) {
  const [hasError, setHasError] = useState(false)
  const fallbackLetter = ticker.charAt(0) || '?'

  useEffect(() => {
    setHasError(false)
  }, [ticker])

  if (!hasLogo || hasError) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        {fallbackLetter}
      </div>
    )
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden">
      <img
        src={`/api/investments/logo/${ticker}`}
        alt={`${ticker} logo`}
        className="h-10 w-10 object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  )
}

export function InvestmentsPage() {
  const viewMode = useViewModeStore((state) => state.viewMode)
  const [holdings, setHoldings] = useState<Holding[]>([])
  const [investorMatches, setInvestorMatches] = useState<InvestorMatch[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showAllInvestors, setShowAllInvestors] = useState(false)
  const [expandedInvestor, setExpandedInvestor] = useState<number | null>(null)
  const [investorDetails, setInvestorDetails] = useState<Record<number, InvestorDetail>>({})
  const [loadingInvestorDetails, setLoadingInvestorDetails] = useState<number | null>(null)
  const [isAddHoldingOpen, setIsAddHoldingOpen] = useState(false)
  const [isManualAccountsLoading, setIsManualAccountsLoading] = useState(false)
  const [manualAccounts, setManualAccounts] = useState<ManualAccountOption[]>([])
  const [selectedManualAccountId, setSelectedManualAccountId] = useState('')
  const [manualAccountName, setManualAccountName] = useState(defaultManualAccountName)
  const [holdingForm, setHoldingForm] = useState({
    ticker: '',
    name: '',
    quantity: '',
    price: '',
    costBasis: '',
  })
  const [holdingFormError, setHoldingFormError] = useState<string | null>(null)
  const [isSavingHolding, setIsSavingHolding] = useState(false)
  const [tickerResults, setTickerResults] = useState<TickerSearchResult[]>([])
  const [isTickerSearching, setIsTickerSearching] = useState(false)
  const [tickerSearchError, setTickerSearchError] = useState<string | null>(null)
  const [showTickerDropdown, setShowTickerDropdown] = useState(false)
  const [isTickerLookupLoading, setIsTickerLookupLoading] = useState(false)
  const tickerSearchTimeoutRef = useRef<number | null>(null)
  const tickerQueryRef = useRef('')
  const tickerAutocompleteRef = useRef<HTMLDivElement | null>(null)
  const lastLookupTickerRef = useRef<string | null>(null)

  // Property (Real Estate) state
  const [properties, setProperties] = useState<Property[]>([])
  const [isAddPropertyOpen, setIsAddPropertyOpen] = useState(false)
  const [propertyForm, setPropertyForm] = useState({
    name: '',
    address: '',
    currentValue: '',
  })
  const [propertyFormError, setPropertyFormError] = useState<string | null>(null)
  const [isSavingProperty, setIsSavingProperty] = useState(false)

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      try {
        const [holdingsResponse, matchesResponse, propertiesResponse] = await Promise.all([
          api.get('/investment_holdings', { params: { view: viewMode } }),
          api.get('/investor-matches'),
          api.get('/properties'),
        ])
        setHoldings(holdingsResponse.data.holdings)
        setInvestorMatches(matchesResponse.data.matches)
        setProperties(propertiesResponse.data.properties || [])
      } catch (error) {
        console.error('Failed to fetch investments:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [viewMode])

  const loadManualAccounts = useCallback(async () => {
    setIsManualAccountsLoading(true)
    try {
      const response = await api.get('/accounts')
      const manualOptions = (response.data.accounts || [])
        .filter(
          (account: { is_manual?: boolean; tracking_mode?: string }) =>
            account.is_manual && account.tracking_mode === 'holdings'
        )
        .map(
          (account: { manual_id?: number; account_id?: string; id?: number | string; name?: string; custom_name?: string | null }) => {
            const resolvedId = resolveManualAccountId(account)
            return resolvedId
              ? {
                  id: resolvedId,
                  name: account.custom_name || account.name || 'Manual Account',
                }
              : null
          }
        )
        .filter((account: ManualAccountOption | null): account is ManualAccountOption => Boolean(account))

      setManualAccounts(manualOptions)
      setSelectedManualAccountId((prev) => {
        if (prev && manualOptions.some((account: ManualAccountOption) => String(account.id) === prev)) {
          return prev
        }
        return manualOptions.length > 0 ? String(manualOptions[0].id) : ''
      })
    } catch (error) {
      console.error('Failed to load manual accounts:', error)
    } finally {
      setIsManualAccountsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAddHoldingOpen) {
      setHoldingFormError(null)
      loadManualAccounts()
    }
  }, [isAddHoldingOpen, loadManualAccounts])

  useEffect(() => {
    tickerQueryRef.current = holdingForm.ticker.trim()
  }, [holdingForm.ticker])

  useEffect(() => {
    if (!isAddHoldingOpen) {
      return
    }

    const query = holdingForm.ticker.trim()
    if (tickerSearchTimeoutRef.current) {
      window.clearTimeout(tickerSearchTimeoutRef.current)
      tickerSearchTimeoutRef.current = null
    }

    if (!query) {
      setTickerResults([])
      setShowTickerDropdown(false)
      setIsTickerSearching(false)
      setTickerSearchError(null)
      return
    }

    setIsTickerSearching(true)
    setTickerSearchError(null)
    setShowTickerDropdown(true)

    tickerSearchTimeoutRef.current = window.setTimeout(async () => {
      try {
        const response = await api.get('/stock/search', {
          params: { q: query, limit: 10 },
        })
        if (tickerQueryRef.current !== query) {
          return
        }
        setTickerResults(response.data.results || [])
        setShowTickerDropdown(true)
      } catch (error) {
        if (tickerQueryRef.current !== query) {
          return
        }
        setTickerResults([])
        setTickerSearchError('Error searching tickers.')
      } finally {
        if (tickerQueryRef.current === query) {
          setIsTickerSearching(false)
        }
      }
    }, 300)

    return () => {
      if (tickerSearchTimeoutRef.current) {
        window.clearTimeout(tickerSearchTimeoutRef.current)
        tickerSearchTimeoutRef.current = null
      }
    }
  }, [holdingForm.ticker, isAddHoldingOpen])

  useEffect(() => {
    if (!showTickerDropdown) {
      return
    }

    const handleClick = (event: MouseEvent) => {
      if (!tickerAutocompleteRef.current) {
        return
      }
      if (!tickerAutocompleteRef.current.contains(event.target as Node)) {
        setShowTickerDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [showTickerDropdown])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await api.post('/refresh_accounts')
      const holdingsResponse = await api.get('/investment_holdings', { params: { view: viewMode } })
      setHoldings(holdingsResponse.data.holdings)
    } catch (error) {
      console.error('Failed to refresh:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const clearTickerAutocomplete = () => {
    setTickerResults([])
    setShowTickerDropdown(false)
    setIsTickerSearching(false)
    setTickerSearchError(null)
  }

  const lookupStockInfo = useCallback(async (rawTicker: string) => {
    const normalized = rawTicker.trim().toUpperCase()
    if (!normalized || lastLookupTickerRef.current === normalized) {
      return
    }

    setIsTickerLookupLoading(true)

    try {
      const response = await api.get(`/stock/lookup/${encodeURIComponent(normalized)}`)
      const price = Number(response.data?.price)
      setHoldingForm((prev) => ({
        ...prev,
        ticker: normalized,
        name: response.data?.name || prev.name || normalized,
        price: Number.isFinite(price) ? price.toFixed(2) : prev.price,
      }))
      setHoldingFormError(null)
      lastLookupTickerRef.current = normalized
    } catch (error) {
      console.error('Failed to lookup stock:', error)
    } finally {
      setIsTickerLookupLoading(false)
    }
  }, [])

  const handleTickerSelect = (result: TickerSearchResult) => {
    setHoldingForm((prev) => ({
      ...prev,
      ticker: result.ticker.toUpperCase(),
      name: result.name || prev.name,
    }))
    setHoldingFormError(null)
    clearTickerAutocomplete()
    lookupStockInfo(result.ticker)
  }

  const handleTickerBlur = () => {
    window.setTimeout(() => {
      clearTickerAutocomplete()
      lookupStockInfo(holdingForm.ticker)
    }, 150)
  }

  const resetHoldingForm = () => {
    setHoldingForm({
      ticker: '',
      name: '',
      quantity: '',
      price: '',
      costBasis: '',
    })
    setManualAccountName(defaultManualAccountName)
    setSelectedManualAccountId('')
    setHoldingFormError(null)
    setIsSavingHolding(false)
    setIsTickerLookupLoading(false)
    clearTickerAutocomplete()
    lastLookupTickerRef.current = null
  }

  const handleAddHoldingSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setHoldingFormError(null)

    const ticker = holdingForm.ticker.trim().toUpperCase()
    const name = holdingForm.name.trim()
    const quantity = Number(holdingForm.quantity)
    const price = Number(holdingForm.price)
    const costBasisInput = holdingForm.costBasis.trim()
    const costBasis = costBasisInput ? Number(costBasisInput) : null

    if (!ticker || !name) {
      setHoldingFormError('Ticker and name are required.')
      return
    }

    if (!Number.isFinite(quantity) || quantity <= 0) {
      setHoldingFormError('Quantity must be greater than zero.')
      return
    }

    if (!Number.isFinite(price) || price <= 0) {
      setHoldingFormError('Price must be greater than zero.')
      return
    }

    if (costBasisInput && !Number.isFinite(costBasis)) {
      setHoldingFormError('Cost basis must be a number.')
      return
    }

    setIsSavingHolding(true)

    try {
      let manualAccountId: number | null = null

      if (manualAccounts.length > 0) {
        const parsedId = Number(selectedManualAccountId)
        manualAccountId = Number.isFinite(parsedId) ? parsedId : null
        if (!manualAccountId) {
          setHoldingFormError('Select a manual account to add this holding.')
          return
        }
      } else {
        const accountName = manualAccountName.trim() || defaultManualAccountName
        const createResponse = await api.post('/manual_accounts', {
          name: accountName,
          tracking_mode: 'holdings',
        })
        manualAccountId = createResponse.data.account?.id ?? null
        if (!manualAccountId) {
          throw new Error('Manual account creation failed')
        }
        setManualAccounts([{ id: manualAccountId, name: accountName }])
        setSelectedManualAccountId(String(manualAccountId))
      }

      await api.post(`/manual_accounts/${manualAccountId}/holdings`, {
        ticker,
        name,
        quantity,
        price,
        cost_basis: costBasis,
      })

      const [holdingsResponse, matchesResponse] = await Promise.all([
        api.get('/investment_holdings'),
        api.get('/investor-matches'),
      ])
      setHoldings(holdingsResponse.data.holdings)
      setInvestorMatches(matchesResponse.data.matches)
      setIsAddHoldingOpen(false)
    } catch (error) {
      console.error('Failed to add holding:', error)
      setHoldingFormError('Unable to add holding. Please try again.')
    } finally {
      setIsSavingHolding(false)
    }
  }

  // Property handlers
  const resetPropertyForm = () => {
    setPropertyForm({
      name: '',
      address: '',
      currentValue: '',
    })
    setPropertyFormError(null)
    setIsSavingProperty(false)
  }

  const handleAddPropertySubmit = async (event: FormEvent) => {
    event.preventDefault()
    setPropertyFormError(null)

    const name = propertyForm.name.trim()
    const address = propertyForm.address.trim()
    const currentValue = Number(propertyForm.currentValue)

    if (!name && !address) {
      setPropertyFormError('Please enter a property name or address.')
      return
    }

    if (!Number.isFinite(currentValue) || currentValue <= 0) {
      setPropertyFormError('Current value must be greater than zero.')
      return
    }

    setIsSavingProperty(true)

    try {
      await api.post('/properties', {
        source: 'manual',
        name: name || address,
        address: address || undefined,
        current_value: currentValue,
      })

      const propertiesResponse = await api.get('/properties')
      setProperties(propertiesResponse.data.properties || [])
      setIsAddPropertyOpen(false)
      resetPropertyForm()
    } catch (error) {
      console.error('Failed to add property:', error)
      setPropertyFormError('Unable to add property. Please try again.')
    } finally {
      setIsSavingProperty(false)
    }
  }

  const totalPropertyValue = properties.reduce((sum, p) => sum + (p.current_value || 0), 0)
  const totalValue = holdings.reduce((sum, h) => sum + h.value, 0)
  const totalCostBasis = holdings.reduce((sum, h) => sum + (h.cost_basis || 0), 0)
  const totalGainLoss = totalValue - totalCostBasis
  const totalGainLossPercent = totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0

  type AggregatedHolding = {
    id: string
    ticker: string
    name: string
    value: number
    quantity: number
    price: number
    costBasis: number | null
    percent: number
    securityType: string | null
    isEtf: boolean
    accounts: Array<{
      accountName: string
      quantity: number
      value: number
      costBasis: number | null
    }>
  }

  const aggregatedHoldings = useMemo((): AggregatedHolding[] => {
    const grouped = new Map<string, {
      id: string
      ticker: string
      name: string
      value: number
      quantity: number
      price: number
      costBasis: number | null
      securityType: string | null
      accounts: Array<{
        accountName: string
        quantity: number
        value: number
        costBasis: number | null
      }>
    }>()

    holdings.forEach((holding) => {
      const ticker = holding.ticker_symbol?.trim().toUpperCase() || ''
      const name = holding.security_name?.trim() || 'Unknown'
      const key = ticker || name.toUpperCase()
      const value = Number.isFinite(holding.value) ? holding.value : 0
      if (value <= 0) return

      const existing = grouped.get(key)
      const accountEntry = {
        accountName: holding.account_name || 'Unknown Account',
        quantity: holding.quantity || 0,
        value: holding.value || 0,
        costBasis: holding.cost_basis,
      }

      if (existing) {
        existing.value += value
        existing.quantity += holding.quantity || 0
        if (holding.cost_basis) {
          existing.costBasis = (existing.costBasis || 0) + holding.cost_basis
        }
        existing.accounts.push(accountEntry)
      } else {
        grouped.set(key, {
          id: key,
          ticker: ticker || name,
          name,
          value,
          quantity: holding.quantity || 0,
          price: holding.price || 0,
          costBasis: holding.cost_basis,
          securityType: holding.security_type,
          accounts: [accountEntry],
        })
      }
    })

    const total = Array.from(grouped.values()).reduce((sum, item) => sum + item.value, 0)
    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        percent: total > 0 ? item.value / total : 0,
        isEtf: item.securityType?.toLowerCase() === 'etf',
      }))
      .sort((a, b) => b.value - a.value)
  }, [holdings])

  // Investment view tab: 'holdings' or 'breakdown'
  const [investmentTab, setInvestmentTab] = useState<'holdings' | 'breakdown'>('holdings')

  // State for expanded holding (to show account breakdown and ETF holdings)
  const [expandedHolding, setExpandedHolding] = useState<string | null>(null)
  const [etfProfiles, setEtfProfiles] = useState<Record<string, EtfProfile>>({})
  const [loadingEtf, setLoadingEtf] = useState<string | null>(null)
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false)

  const loadEtfProfile = useCallback(async (ticker: string) => {
    if (etfProfiles[ticker] || loadingEtf === ticker) return
    setLoadingEtf(ticker)
    try {
      const response = await api.get(`/etf/holdings/${encodeURIComponent(ticker)}`)
      setEtfProfiles((prev) => ({ ...prev, [ticker]: response.data }))
    } catch (error) {
      console.error('Failed to load ETF profile:', error)
    } finally {
      setLoadingEtf(null)
    }
  }, [etfProfiles, loadingEtf])

  const handleHoldingClick = useCallback((holding: AggregatedHolding) => {
    const newExpanded = expandedHolding === holding.id ? null : holding.id
    setExpandedHolding(newExpanded)
    if (newExpanded && holding.isEtf && holding.ticker) {
      loadEtfProfile(holding.ticker)
    }
  }, [expandedHolding, loadEtfProfile])

  const loadInvestorDetails = useCallback(async (investorId: number) => {
    if (investorDetails[investorId] || loadingInvestorDetails === investorId) return
    setLoadingInvestorDetails(investorId)
    try {
      const response = await api.get(`/famous-investors/${investorId}`)
      setInvestorDetails((prev) => ({ ...prev, [investorId]: response.data }))
    } catch (error) {
      console.error('Failed to load investor details:', error)
    } finally {
      setLoadingInvestorDetails(null)
    }
  }, [investorDetails, loadingInvestorDetails])

  const handleInvestorClick = useCallback((investorId: number) => {
    const newExpanded = expandedInvestor === investorId ? null : investorId
    setExpandedInvestor(newExpanded)
    if (newExpanded) {
      loadInvestorDetails(investorId)
    }
  }, [expandedInvestor, loadInvestorDetails])

  // For bubble chart (simpler structure)
  const bubbleChartHoldings = useMemo(() => {
    return aggregatedHoldings.map((h) => ({
      id: h.id,
      label: h.ticker || h.name,
      value: h.value,
      percent: h.percent,
    }))
  }, [aggregatedHoldings])

  // Get all ETF tickers from holdings
  const etfTickers = useMemo(() => {
    return aggregatedHoldings
      .filter((h) => h.isEtf && h.ticker)
      .map((h) => h.ticker)
  }, [aggregatedHoldings])

  // Load all ETF profiles when switching to breakdown tab
  useEffect(() => {
    if (investmentTab !== 'breakdown' || etfTickers.length === 0) return

    const loadAllEtfProfiles = async () => {
      const tickersToLoad = etfTickers.filter((ticker) => !etfProfiles[ticker])
      if (tickersToLoad.length === 0) return

      setIsLoadingBreakdown(true)
      try {
        const responses = await Promise.allSettled(
          tickersToLoad.map((ticker) =>
            api.get(`/etf/holdings/${encodeURIComponent(ticker)}`)
          )
        )
        const newProfiles: Record<string, EtfProfile> = {}
        responses.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            newProfiles[tickersToLoad[idx]] = result.value.data
          }
        })
        setEtfProfiles((prev) => ({ ...prev, ...newProfiles }))
      } catch (error) {
        console.error('Failed to load ETF profiles:', error)
      } finally {
        setIsLoadingBreakdown(false)
      }
    }

    loadAllEtfProfiles()
  }, [investmentTab, etfTickers, etfProfiles])

  // Compute true exposure breakdown
  type TickerExposure = {
    ticker: string
    name: string
    value: number
    sources: Array<{ source: string; value: number }>
  }

  const tickerBreakdown = useMemo((): TickerExposure[] => {
    const exposureMap = new Map<string, TickerExposure>()

    // Add direct holdings (non-ETF)
    aggregatedHoldings
      .filter((h) => !h.isEtf)
      .forEach((holding) => {
        const ticker = holding.ticker.toUpperCase()
        const existing = exposureMap.get(ticker)
        if (existing) {
          existing.value += holding.value
          existing.sources.push({ source: 'Direct', value: holding.value })
        } else {
          exposureMap.set(ticker, {
            ticker,
            name: holding.name,
            value: holding.value,
            sources: [{ source: 'Direct', value: holding.value }],
          })
        }
      })

    // Add ETF underlying holdings
    aggregatedHoldings
      .filter((h) => h.isEtf && h.ticker)
      .forEach((etfHolding) => {
        const profile = etfProfiles[etfHolding.ticker]
        if (!profile?.holdings?.length) return

        profile.holdings.slice(0, 20).forEach((underlying) => {
          if (!underlying.symbol || underlying.weight == null) return
          const ticker = underlying.symbol.toUpperCase()
          const exposedValue = etfHolding.value * underlying.weight
          const sourceName = `via ${etfHolding.ticker}`

          const existing = exposureMap.get(ticker)
          if (existing) {
            existing.value += exposedValue
            existing.sources.push({ source: sourceName, value: exposedValue })
          } else {
            exposureMap.set(ticker, {
              ticker,
              name: underlying.description || ticker,
              value: exposedValue,
              sources: [{ source: sourceName, value: exposedValue }],
            })
          }
        })
      })

    return Array.from(exposureMap.values())
      .sort((a, b) => b.value - a.value)
      .slice(0, 50)
  }, [aggregatedHoldings, etfProfiles])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-40" />
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Dialog
        open={isAddHoldingOpen}
        onOpenChange={(open) => {
          setIsAddHoldingOpen(open)
          if (!open) {
            resetHoldingForm()
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Holding</DialogTitle>
            <DialogDescription>
              Track a manual investment holding in your portfolio.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleAddHoldingSubmit}>
            {isManualAccountsLoading ? (
              <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                Loading manual accounts...
              </div>
            ) : manualAccounts.length > 0 ? (
              <div className="grid gap-2">
                <Label htmlFor="manual-account">Manual account</Label>
                <Select
                  value={selectedManualAccountId}
                  onValueChange={setSelectedManualAccountId}
                >
                  <SelectTrigger id="manual-account">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {manualAccounts.map((account) => (
                      <SelectItem key={account.id} value={String(account.id)}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid gap-2">
                <Label htmlFor="manual-account-name">Account name</Label>
                <Input
                  id="manual-account-name"
                  value={manualAccountName}
                  onChange={(event) => setManualAccountName(event.target.value)}
                  placeholder="Manual Holdings"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  We'll create a manual investment account for this holding.
                </p>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="holding-ticker">Ticker</Label>
                <div className="relative" ref={tickerAutocompleteRef}>
                  <Input
                    id="holding-ticker"
                    value={holdingForm.ticker}
                    onChange={(event) => {
                      setHoldingForm((prev) => ({
                        ...prev,
                        ticker: event.target.value.toUpperCase(),
                      }))
                      setHoldingFormError(null)
                      lastLookupTickerRef.current = null
                    }}
                    onBlur={handleTickerBlur}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        clearTickerAutocomplete()
                      }
                    }}
                    placeholder="AAPL"
                    autoComplete="off"
                  />
                  {showTickerDropdown && (
                    <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
                      {isTickerSearching ? (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          Searching...
                        </div>
                      ) : tickerSearchError ? (
                        <div className="px-3 py-2 text-sm text-danger">
                          {tickerSearchError}
                        </div>
                      ) : tickerResults.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          No results found.
                        </div>
                      ) : (
                        <div className="max-h-56 overflow-y-auto">
                          {tickerResults.map((result) => (
                            <button
                              key={`${result.ticker}-${result.name}`}
                              type="button"
                              className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-gray-50 dark:hover:bg-gray-800"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleTickerSelect(result)}
                            >
                              <TickerLogo
                                ticker={result.ticker}
                                hasLogo={result.has_logo}
                              />
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  {result.ticker}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {result.name}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="holding-name">Name</Label>
                <Input
                  id="holding-name"
                  value={holdingForm.name}
                  onChange={(event) =>
                    setHoldingForm((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  placeholder={isTickerLookupLoading ? 'Loading...' : 'Apple Inc.'}
                  disabled={isTickerLookupLoading}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label htmlFor="holding-quantity">Quantity</Label>
                <Input
                  id="holding-quantity"
                  type="number"
                  min="0"
                  step="any"
                  value={holdingForm.quantity}
                  onChange={(event) =>
                    setHoldingForm((prev) => ({
                      ...prev,
                      quantity: event.target.value,
                    }))
                  }
                  placeholder="10"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="holding-price">Price</Label>
                <Input
                  id="holding-price"
                  type="number"
                  min="0"
                  step="any"
                  value={holdingForm.price}
                  onChange={(event) =>
                    setHoldingForm((prev) => ({
                      ...prev,
                      price: event.target.value,
                    }))
                  }
                  placeholder={isTickerLookupLoading ? 'Loading...' : '195.50'}
                  disabled={isTickerLookupLoading}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="holding-cost-basis">Cost basis</Label>
                <Input
                  id="holding-cost-basis"
                  type="number"
                  min="0"
                  step="any"
                  value={holdingForm.costBasis}
                  onChange={(event) =>
                    setHoldingForm((prev) => ({
                      ...prev,
                      costBasis: event.target.value,
                    }))
                  }
                  placeholder="1955.00"
                />
              </div>
            </div>

            {holdingFormError && (
              <p className="text-sm text-danger">{holdingFormError}</p>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddHoldingOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSavingHolding || isManualAccountsLoading}
              >
                {isSavingHolding ? 'Saving...' : 'Save Holding'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Investments
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setIsAddHoldingOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Holding
          </Button>
          <Button variant="outline" onClick={() => setIsAddPropertyOpen(true)}>
            <Home className="mr-2 h-4 w-4" />
            Add Property
          </Button>
        </div>
      </div>

      {/* Add Property Modal */}
      <Dialog
        open={isAddPropertyOpen}
        onOpenChange={(open) => {
          setIsAddPropertyOpen(open)
          if (!open) {
            resetPropertyForm()
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Property</DialogTitle>
            <DialogDescription>
              Add a real estate property to your portfolio.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleAddPropertySubmit}>
            <div className="grid gap-2">
              <Label htmlFor="property-name">Property Name</Label>
              <Input
                id="property-name"
                value={propertyForm.name}
                onChange={(e) =>
                  setPropertyForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Primary Residence"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="property-address">Address (optional)</Label>
              <Input
                id="property-address"
                value={propertyForm.address}
                onChange={(e) =>
                  setPropertyForm((prev) => ({ ...prev, address: e.target.value }))
                }
                placeholder="e.g., 123 Main St, City, State"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="property-value">Current Value</Label>
              <Input
                id="property-value"
                type="number"
                min="0"
                step="any"
                value={propertyForm.currentValue}
                onChange={(e) =>
                  setPropertyForm((prev) => ({ ...prev, currentValue: e.target.value }))
                }
                placeholder="500000"
              />
            </div>

            {propertyFormError && (
              <p className="text-sm text-danger">{propertyFormError}</p>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddPropertyOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSavingProperty}>
                {isSavingProperty ? 'Saving...' : 'Save Property'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Portfolio Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Portfolio Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(totalValue)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {holdings.length} holdings
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Total Gain/Loss
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {totalGainLoss >= 0 ? (
                <TrendingUp className="h-5 w-5 text-success" />
              ) : (
                <TrendingDown className="h-5 w-5 text-danger" />
              )}
              <p
                className={`text-2xl font-bold ${
                  totalGainLoss >= 0 ? 'text-success' : 'text-danger'
                }`}
              >
                {totalGainLoss >= 0 ? '+' : ''}
                {formatCurrency(totalGainLoss)}
              </p>
            </div>
            <p
              className={`text-sm ${
                totalGainLoss >= 0 ? 'text-success' : 'text-danger'
              }`}
            >
              {totalGainLossPercent >= 0 ? '+' : ''}
              {totalGainLossPercent.toFixed(2)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Cost Basis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(totalCostBasis)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total invested
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Real Estate Section */}
      {properties.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5" />
              Real Estate
            </CardTitle>
            <Badge variant="secondary">
              {formatCurrency(totalPropertyValue)}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {properties.map((property) => (
                <div
                  key={property.id}
                  className="flex items-center gap-4 py-4"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Home className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {property.name || property.address || 'Property'}
                    </p>
                    {property.address && property.name && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {property.address}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(property.current_value)}
                    </p>
                    {property.net_equity !== undefined && property.net_equity !== property.current_value && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Equity: {formatCurrency(property.net_equity)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Holdings Bubble Chart */}
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Holdings Bubble Map</CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Top holdings sized by portfolio share.
            </p>
          </div>
          <Badge variant="secondary">
            {aggregatedHoldings.length} holdings
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-900/40">
            <HoldingsBubbleChart holdings={bubbleChartHoldings} />
          </div>
        </CardContent>
      </Card>

      {/* Investor Matches */}
      {investorMatches.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Investors You Match With
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllInvestors(!showAllInvestors)}
            >
              {showAllInvestors ? 'Show less' : 'Show all'}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {(showAllInvestors ? investorMatches : investorMatches.slice(0, 3)).map(
                (match, index) => {
                  const investorId = match.investor?.id ?? index
                  const isExpanded = expandedInvestor === investorId

                  return (
                    <div
                      key={investorId}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <button
                        onClick={() => handleInvestorClick(investorId)}
                        className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <Avatar className="h-12 w-12">
                          {match.investor?.photo_url && <AvatarImage src={match.investor.photo_url} />}
                          <AvatarFallback>
                            {(match.investor?.name || 'U').charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-gray-100">
                            {match.investor?.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {match.explanation?.shared_holdings?.length ?? 0} shared holdings
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">
                            {Math.round(match.scores?.overall ?? 0)}% match
                          </Badge>
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 bg-gray-50/50 dark:bg-gray-800/30">
                          {/* Bio */}
                          {match.investor?.bio && (
                            <div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {match.investor.bio}
                              </p>
                            </div>
                          )}

                          {/* Category */}
                          {match.investor?.category && (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{match.investor.category}</Badge>
                            </div>
                          )}

                          {/* Score breakdown */}
                          <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                              Match Breakdown
                            </p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Holdings Overlap</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {Math.round(match.scores?.holdings_overlap ?? 0)}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Concentration</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {Math.round(match.scores?.concentration ?? 0)}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Market Cap</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {Math.round(match.scores?.market_cap ?? 0)}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Growth/Value</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {Math.round(match.scores?.growth_value ?? 0)}%
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Shared holdings */}
                          {match.explanation?.shared_holdings && match.explanation.shared_holdings.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                Shared Holdings
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {match.explanation.shared_holdings.slice(0, 8).map((holding) => (
                                  <Badge key={holding.ticker} variant="outline" className="text-xs">
                                    {holding.ticker}
                                  </Badge>
                                ))}
                                {match.explanation.shared_holdings.length > 8 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{match.explanation.shared_holdings.length - 8} more
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}

                          {/* AI Verdict */}
                          {match.explanation?.verdict && (
                            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
                              <p className="text-xs text-primary-700 dark:text-primary-300">
                                {match.explanation.verdict}
                              </p>
                            </div>
                          )}

                          {/* Recent Trades */}
                          {(() => {
                            const details = investorDetails[investorId]
                            const isLoadingDetails = loadingInvestorDetails === investorId
                            const trades = details?.trades || []

                            if (isLoadingDetails) {
                              return (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                    Recent Trades
                                  </p>
                                  <div className="space-y-2">
                                    {[1, 2, 3].map((i) => (
                                      <Skeleton key={i} className="h-8 w-full" />
                                    ))}
                                  </div>
                                </div>
                              )
                            }

                            if (trades.length > 0) {
                              return (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                    Recent Trades
                                  </p>
                                  <div className="space-y-1">
                                    {trades.slice(0, 5).map((trade) => (
                                      <div
                                        key={trade.id}
                                        className="flex items-center justify-between text-xs bg-gray-100 dark:bg-gray-800 rounded px-2 py-1.5"
                                      >
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={cn(
                                              'px-1.5 py-0.5 rounded text-[10px] font-medium',
                                              trade.transaction_type?.toLowerCase().includes('buy') || trade.transaction_type?.toLowerCase().includes('purchase')
                                                ? 'bg-success/20 text-success'
                                                : 'bg-danger/20 text-danger'
                                            )}
                                          >
                                            {trade.transaction_type?.toLowerCase().includes('buy') || trade.transaction_type?.toLowerCase().includes('purchase') ? 'BUY' : 'SELL'}
                                          </span>
                                          <span className="font-medium text-gray-900 dark:text-gray-100">
                                            {trade.ticker}
                                          </span>
                                          {trade.company_name && (
                                            <span className="text-gray-500 dark:text-gray-400 truncate max-w-24">
                                              {trade.company_name}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-right text-gray-500 dark:text-gray-400">
                                          {trade.amount_range || (trade.amount ? `$${trade.amount.toLocaleString()}` : '')}
                                          <span className="ml-2">
                                            {new Date(trade.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                    {trades.length > 5 && (
                                      <p className="text-[10px] text-gray-400 text-center pt-1">
                                        +{trades.length - 5} more trades
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            return null
                          })()}
                        </div>
                      )}
                    </div>
                  )
                }
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Holdings List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Holdings</CardTitle>
          <div className="flex gap-1">
            <Button
              variant={investmentTab === 'holdings' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInvestmentTab('holdings')}
            >
              Direct Holdings
            </Button>
            <Button
              variant={investmentTab === 'breakdown' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInvestmentTab('breakdown')}
            >
              ETF Breakdown
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {investmentTab === 'breakdown' && (
            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
              This view expands your ETF holdings into their top underlying stocks and combines them with your direct holdings. For example, if you own Apple directly and also through VOO (S&P 500 ETF), you'll see your total Apple exposure in one row. Values are weighted by each stock's proportion in the ETF.
            </div>
          )}

          {investmentTab === 'breakdown' ? (
            isLoadingBreakdown ? (
              <div className="py-12 text-center">
                <RefreshCw className="mx-auto mb-4 h-8 w-8 animate-spin text-gray-400" />
                <p className="text-sm text-gray-500">Loading ETF data...</p>
              </div>
            ) : tickerBreakdown.length === 0 ? (
              <div className="py-12 text-center">
                <TrendingUp className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                <p className="text-gray-500">No holdings to analyze.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {tickerBreakdown.map((exposure) => (
                  <div key={exposure.ticker} className="flex items-center gap-4 py-4">
                    <TickerLogo ticker={exposure.ticker} hasLogo />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {exposure.ticker}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {exposure.name}
                      </p>
                      {exposure.sources.length > 1 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {exposure.sources.map((source, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {source.source}: {formatCurrency(source.value)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {formatCurrency(exposure.value)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {((exposure.value / totalValue) * 100).toFixed(1)}% of portfolio
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : aggregatedHoldings.length === 0 ? (
            <div className="py-12 text-center">
              <TrendingUp className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                No holdings yet
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Link an investment account or add holdings manually.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {aggregatedHoldings.map((holding) => {
                const gainLoss = holding.costBasis
                  ? holding.value - holding.costBasis
                  : null
                const gainLossPercent =
                  gainLoss !== null && holding.costBasis
                    ? (gainLoss / holding.costBasis) * 100
                    : null
                const isExpanded = expandedHolding === holding.id
                const etfProfile = holding.isEtf ? etfProfiles[holding.ticker] : null

                return (
                  <div key={holding.id}>
                    {/* Main holding row */}
                    <button
                      onClick={() => handleHoldingClick(holding)}
                      className="flex w-full items-center gap-4 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      {/* Logo */}
                      <TickerLogo ticker={holding.ticker} hasLogo />

                      {/* Ticker and name */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 dark:text-gray-100">
                            {holding.ticker}
                          </p>
                          {holding.isEtf && (
                            <Badge variant="outline" className="text-xs">
                              ETF
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {holding.name}
                        </p>
                      </div>

                      {/* Shares */}
                      <div className="hidden sm:block text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {holding.quantity.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4,
                          })} shares
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          @ {formatCurrency(holding.price)}
                        </p>
                      </div>

                      {/* Value and gain/loss */}
                      <div className="text-right min-w-[100px]">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(holding.value)}
                        </p>
                        {gainLoss !== null ? (
                          <p
                            className={cn(
                              'text-sm',
                              gainLoss >= 0 ? 'text-success' : 'text-danger'
                            )}
                          >
                            {gainLoss >= 0 ? '+' : ''}
                            {formatCurrency(gainLoss)}
                            {gainLossPercent !== null && (
                              <span className="ml-1">
                                ({gainLossPercent >= 0 ? '+' : ''}{gainLossPercent.toFixed(1)}%)
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">
                            {(holding.percent * 100).toFixed(1)}% of portfolio
                          </p>
                        )}
                      </div>

                      {/* Expand indicator */}
                      <div className="text-gray-400">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </div>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="pb-4 pl-14 pr-4 space-y-4">
                        {/* Price Chart */}
                        {holding.ticker && (
                          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                            <PriceChart ticker={holding.ticker} />
                          </div>
                        )}

                        {/* Stock details for non-ETF */}
                        {!holding.isEtf && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Current Price</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(holding.price)}
                              </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Total Shares</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {holding.quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                              </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Market Value</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(holding.value)}
                              </p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                              <p className="text-xs text-gray-500 dark:text-gray-400">Portfolio Weight</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {(holding.percent * 100).toFixed(2)}%
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Account breakdown - always show */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {holding.accounts.length > 1 ? 'Account Breakdown' : 'Account'}
                          </p>
                          <div className="space-y-2">
                            {holding.accounts.map((account, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2"
                              >
                                <span className="text-gray-600 dark:text-gray-400">
                                  {account.accountName}
                                </span>
                                <div className="text-right">
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {formatCurrency(account.value)}
                                  </span>
                                  <span className="text-gray-500 dark:text-gray-400 ml-2">
                                    ({account.quantity.toLocaleString(undefined, {
                                      maximumFractionDigits: 4,
                                    })} shares)
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* ETF Holdings breakdown */}
                        {holding.isEtf && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              ETF Top Holdings
                            </p>
                            {loadingEtf === holding.ticker ? (
                              <div className="space-y-2">
                                {[1, 2, 3, 4, 5].map((i) => (
                                  <Skeleton key={i} className="h-10 w-full" />
                                ))}
                              </div>
                            ) : etfProfile?.holdings && etfProfile.holdings.length > 0 ? (
                              <div className="space-y-2">
                                {etfProfile.holdings.slice(0, 10).map((etfHolding, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2"
                                  >
                                    <TickerLogo
                                      ticker={etfHolding.symbol || '?'}
                                      hasLogo={!!etfHolding.symbol}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                                        {etfHolding.symbol || 'N/A'}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {etfHolding.description || etfHolding.symbol || 'Unknown'}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {etfHolding.weight != null
                                          ? `${(etfHolding.weight * 100).toFixed(2)}%`
                                          : '—'}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                                {etfProfile.holdings.length > 10 && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center pt-2">
                                    + {etfProfile.holdings.length - 10} more holdings
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                ETF holdings data unavailable
                              </p>
                            )}

                            {/* ETF Profile info */}
                            {etfProfile && (
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                                {etfProfile.netAssets && (
                                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Net Assets</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {etfProfile.netAssets}
                                    </p>
                                  </div>
                                )}
                                {etfProfile.netExpenseRatio && (
                                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Expense Ratio</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {etfProfile.netExpenseRatio}
                                    </p>
                                  </div>
                                )}
                                {etfProfile.dividendYield && (
                                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Dividend Yield</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {etfProfile.dividendYield}
                                    </p>
                                  </div>
                                )}
                                {etfProfile.inceptionDate && (
                                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Inception</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {etfProfile.inceptionDate}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
